import FileUpload from '@/components/FormFields/FileUpload/FileUpload.tsx';
import { DashboardContext } from '@/contexts/DashboardContext.tsx';
import { useAllowedFonts } from '@/hooks/useAllowedFonts';
import { AccountBrand, AssetData } from '@superfunnel/types';
import ColorPickerField from '@superfunnel/ui-core/ColorPickerField';
import Checkbox from '@superfunnel/ui-core/v2/Checkbox';
import CodeInput from '@superfunnel/ui-core/v2/CodeInput';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { isValidCss } from '@superfunnel/utils';
import { toCSS, toJSON } from 'css-convert-json';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import { useDynamicPageSettingsContext } from './DynamicPageSettingsContext';
import { DynamicPageSettingsModalTabWrapper } from './DynamicPageSettingsModalTabWrapper';

export const Design = () => {
  const { accountBrand } = useContext(DashboardContext);
  const { dynamicPage, updatePage } = useDynamicPageSettingsContext();
  const [edit, setEdit] = useState<boolean>(false);
  const [logo, setLogo] = useState<AssetData | undefined>(undefined);
  const [font, setFont] = useState<string>('');
  const [secondaryFont, setSecondaryFont] = useState<string>('');
  const [buttonCSS, setButtonCSS] = useState<string>('');
  const [shadow, setShadow] = useState<boolean>(true);
  const allowedFonts = useAllowedFonts();
  const [primaryColor, setPrimaryColor] = useState<string | undefined>(
    '#000000',
  );
  const [secondaryColor, setSecondaryColor] = useState<string | undefined>(
    '#000000',
  );
  const [backgroundColor, setBackgroundColor] = useState<string | undefined>(
    undefined,
  );
  const [otherColors, setOtherColors] = useState<
    NonNullable<AccountBrand['otherColors']>
  >([]);
  const [borderRadius, setBorderRadius] = useState<number>(6);

  // Helper functions for managing other colors
  const updateOtherColor = useCallback((index: number, color: string) => {
    setOtherColors((prev) => {
      const newColors = [...prev];

      // If we're setting a color in the empty slot (last index), extend the array
      if (index >= newColors.length && color && color.trim() !== '') {
        newColors.push({ id: uid(8), color, label: `Color ${index + 1}` });
      } else if (index < newColors.length) {
        if (color && color.trim() !== '') {
          newColors[index] = {
            id: newColors[index].id,
            color,
            label: newColors[index].label,
          };
        } else {
          // Remove empty colors
          newColors.splice(index, 1);
        }
      }

      return newColors;
    });
  }, []);

  const removeOtherColor = useCallback((index: number) => {
    setOtherColors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Always ensure there's one empty slot for adding new colors
  const otherColorsWithEmpty = useMemo(() => {
    const nonEmptyColors = otherColors.filter(
      (color) => color && color.color.trim() !== '',
    );
    return [
      ...nonEmptyColors,
      { id: uid(8), color: '', label: 'Color ' + (nonEmptyColors.length + 1) },
    ];
  }, [otherColors]);

  const setValues = useCallback(() => {
    setFont(dynamicPage?.brand?.font ?? accountBrand?.font ?? 'Inter');

    setSecondaryFont(
      dynamicPage?.brand?.secondaryFont ??
        (accountBrand as any)?.secondaryFont ??
        'Inter',
    );

    setShadow(dynamicPage?.brand?.shadow === true);

    setPrimaryColor(
      dynamicPage?.brand?.primaryColor ??
        accountBrand?.primaryColor ??
        '#000000',
    );

    setSecondaryColor(
      dynamicPage?.brand?.secondaryColor ??
        accountBrand?.secondaryColor ??
        '#000000',
    );

    setBackgroundColor(
      dynamicPage?.brand?.backgroundColor ??
        accountBrand?.backgroundColor ??
        undefined,
    );

    // Handle other colors with proper parsing
    const dynamicPageOtherColors = (() => {
      const colors = dynamicPage?.brand?.otherColors;
      if (colors) {
        try {
          const parsed =
            typeof colors === 'string' ? JSON.parse(colors) : colors;
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    })();

    const accountOtherColors = (() => {
      if (accountBrand?.otherColors) {
        try {
          const parsed =
            typeof accountBrand.otherColors === 'string'
              ? JSON.parse(accountBrand.otherColors)
              : accountBrand.otherColors;
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    })();

    setOtherColors(
      dynamicPageOtherColors.length > 0
        ? dynamicPageOtherColors
        : accountOtherColors,
    );

    setBorderRadius(
      dynamicPage?.brand?.borderRadius ?? accountBrand?.borderRadius ?? 6,
    );
    setLogo(dynamicPage?.brand?.logo ?? accountBrand?.logo ?? undefined);

    // Handle button CSS with proper error handling
    try {
      const dynamicPageCSS = dynamicPage?.brand?.buttonCSS;
      const accountCSS = accountBrand?.buttonCSS;

      if (dynamicPageCSS) {
        setButtonCSS(toCSS(dynamicPageCSS as any).trim());
      } else if (accountCSS) {
        setButtonCSS(toCSS(accountCSS as any).trim());
      } else {
        setButtonCSS('');
      }
    } catch (error) {
      console.error('Error loading CSS:', error);
      setButtonCSS('');
    }
  }, [accountBrand, dynamicPage]);

  useEffect(() => {
    setValues();
  }, [setValues]);

  const normalizeCss = (s: string = '') => s.replace(/\s+/g, ' ').trim();

  useEffect(() => {
    if (!accountBrand) return;
    const brand = dynamicPage?.brand;

    // Get CSS from server with proper error handling
    let cssFromServer = '';
    try {
      if (brand?.buttonCSS) {
        cssFromServer = toCSS(brand.buttonCSS as any).trim();
      } else if (accountBrand?.buttonCSS) {
        cssFromServer = toCSS(accountBrand.buttonCSS as any).trim();
      }
    } catch (error) {
      console.error('Error converting server CSS:', error);
      cssFromServer = '';
    }

    const logoUrl = brand?.logo || accountBrand.logo || undefined;
    const primary =
      brand?.primaryColor || accountBrand.primaryColor || '#000000';
    const secondary =
      brand?.secondaryColor || accountBrand.secondaryColor || '#000000';
    const background =
      brand?.backgroundColor || accountBrand.backgroundColor || undefined;
    const fontFamily = brand?.font || accountBrand.font || 'Inter';
    const secondaryFontFamily =
      brand?.secondaryFont || (accountBrand as any)?.secondaryFont || 'Inter';
    const radius = brand?.borderRadius || accountBrand.borderRadius || 6;
    const shadowState = brand?.shadow ?? true;

    // Compare other colors
    const serverOtherColors = (() => {
      const brandColors = brand?.otherColors;
      const accountColors = accountBrand?.otherColors;

      if (brandColors) {
        try {
          const parsed =
            typeof brandColors === 'string'
              ? JSON.parse(brandColors)
              : brandColors;
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }

      if (accountColors) {
        try {
          const parsed =
            typeof accountColors === 'string'
              ? JSON.parse(accountColors)
              : accountColors;
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }

      return [];
    })();

    const otherColorsChanged =
      JSON.stringify(otherColors) !== JSON.stringify(serverOtherColors);

    setEdit(
      logoUrl?.url !== logo?.url ||
        primaryColor !== primary ||
        secondaryColor !== secondary ||
        backgroundColor !== background ||
        font !== fontFamily ||
        secondaryFont !== secondaryFontFamily ||
        borderRadius !== radius ||
        normalizeCss(buttonCSS) !== normalizeCss(cssFromServer) ||
        shadowState !== shadow ||
        otherColorsChanged,
    );
  }, [
    dynamicPage,
    accountBrand,
    font,
    secondaryFont,
    logo,
    primaryColor,
    secondaryColor,
    backgroundColor,
    buttonCSS,
    borderRadius,
    shadow,
    otherColors,
  ]);

  const onSave = () => {
    // Helper function to clean values for Firestore
    const cleanValue = (value: any, isColor = false) => {
      if (value === undefined) {
        return undefined; // Will be filtered out
      }
      if (value === null) {
        return null;
      }
      if (value === '' && isColor) {
        return null; // Convert empty color strings to null for Firestore
      }
      if (value === '') {
        return undefined; // Will be filtered out for non-color fields
      }
      return value;
    };

    // Process buttonCSS with proper validation and conversion
    let processedButtonCSS = undefined;
    if (buttonCSS && buttonCSS.trim() !== '') {
      if (isValidCss(buttonCSS)) {
        try {
          processedButtonCSS = toJSON(buttonCSS);
        } catch (error) {
          console.error('Error converting CSS to JSON:', error);
          processedButtonCSS = undefined; // Don't save invalid CSS
        }
      } else {
        console.warn('Invalid CSS detected, not saving:', buttonCSS);
        processedButtonCSS = undefined; // Don't save invalid CSS
      }
    } else {
      // Explicitly handle empty CSS - we want to save null to clear it
      processedButtonCSS = null;
    }

    // Create the update object with proper cleaning
    const rawData = {
      font: cleanValue(font),
      secondaryFont: cleanValue(secondaryFont),
      logo: cleanValue(logo),
      primaryColor: cleanValue(primaryColor, true),
      secondaryColor: cleanValue(secondaryColor, true),
      backgroundColor: cleanValue(backgroundColor, true),
      buttonCSS: processedButtonCSS,
      borderRadius: cleanValue(borderRadius),
      shadow: cleanValue(shadow),
      otherColors: otherColors.filter(
        (color) => color && color.color.trim() !== '',
      ),
    };

    // Filter out undefined values but keep null values (null explicitly clears fields)
    const updateData: any = {};
    Object.keys(rawData).forEach((key) => {
      const value = rawData[key as keyof typeof rawData];
      if (value !== undefined) {
        updateData[key] = value;
      }
    });

    updatePage({
      brand: {
        ...dynamicPage?.brand,
        ...updateData,
      },
    });
  };

  return (
    <DynamicPageSettingsModalTabWrapper
      edit={edit}
      onDiscard={setValues}
      onSave={onSave}
    >
      <div className={'space-y-2 md:col-span-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">Logo</h2>
        <FileUpload image={logo} onSelected={(image) => setLogo(image)} />
      </div>
      <div className={'space-y-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Primary Color
        </h2>
        <ColorPickerField
          variant="field"
          value={primaryColor}
          debounce
          onChange={(v) => setPrimaryColor(v)}
        />
      </div>
      <div className={'space-y-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Secondary Color
        </h2>
        <ColorPickerField
          variant="field"
          value={secondaryColor}
          debounce
          onChange={(v) => setSecondaryColor(v)}
        />
      </div>
      <div className={'space-y-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Background Color
        </h2>
        <ColorPickerField
          variant="field"
          value={backgroundColor}
          debounce
          onChange={(v) => setBackgroundColor(v)}
        />
      </div>
      <div className={'space-y-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Other colors
        </h2>
        <div className="flex flex-wrap gap-3">
          {otherColorsWithEmpty.map((color, index) => {
            const isEmptySlot = color.color === '';
            const hasDelete = !isEmptySlot && index < otherColors.length;
            return (
              <ColorPickerField
                key={isEmptySlot ? 'empty-slot' : `color-${index}`}
                className={'rounded-2lg size-10 shadow-md'}
                value={color.color}
                debounce
                allowDelete={hasDelete}
                onDelete={() => removeOtherColor(index)}
                onChange={(v) => updateOtherColor(index, v)}
              />
            );
          })}
        </div>
      </div>
      <div className={'space-y-1 md:col-span-2'}>
        <CodeInput
          title={'Custom Button CSS'}
          titleClassName={'text-base-700 font-inter text-sm font-medium'}
          value={buttonCSS.toString()}
          onChange={(value) => {
            console.log('buttonCSS changed:', value);
            setButtonCSS(value);
            if (isValidCss(value)) {
              console.log('updated!!!', value, toJSON(value));
            }
          }}
          language={'css'}
          inlineLines={5}
          placeholder={
            'text-align: center;\nfont-size: 16px;\npadding: 12px 24px;\nborder-radius: 8px;\nbackground-color: #007bff;'
          }
        />
      </div>
      <div className={'space-y-1'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Primary Font
        </h2>
        <Select value={font} onValueChange={(selected) => setFont(selected)}>
          <SelectTrigger className={'rounded-2lg font-inter h-10! w-full'}>
            <SelectValue placeholder="Select a font" />
          </SelectTrigger>
          <SelectContent className={'font-inter'}>
            {allowedFonts?.map((font) => (
              <SelectItem value={font} key={font}>
                {font}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className={'space-y-1'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Secondary Font
        </h2>
        <Select
          value={secondaryFont}
          onValueChange={(selected) => setSecondaryFont(selected)}
        >
          <SelectTrigger className={'rounded-2lg font-inter h-10! w-full'}>
            <SelectValue placeholder="Select a secondary font" />
          </SelectTrigger>
          <SelectContent className={'font-inter'}>
            {allowedFonts?.map((font) => (
              <SelectItem value={font} key={font}>
                {font}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className={'space-y-2 md:col-span-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Border Radius
        </h2>
        <Input
          icon={
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="32" height="32" rx="9" fill="#F2F4F7" />
              <path
                d="M6 6H14C20.6274 6 26 11.3726 26 18V26"
                stroke="#637083"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          }
          className={'font-inter rounded-2lg h-10 p-1'}
          value={`${borderRadius}`}
          onChange={(e) => {
            const newVal = e.target.value;

            try {
              const num = parseFloat(newVal || '0');
              setBorderRadius(num);
            } catch (_) {
              /* empty */
            }
          }}
        />
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="show-shadow"
          checked={shadow}
          onCheckedChange={(checked) => {
            setShadow(checked as boolean);
          }}
        />
        <Label
          htmlFor="show-shadow"
          className={'font-inter text-base font-normal'}
        >
          Show Shadows
        </Label>
      </div>
    </DynamicPageSettingsModalTabWrapper>
  );
};
